import React, { useMemo } from 'react'
import styled, { css } from 'styled-components'
import { Table } from '@dataplace.ai/ui-components/organisms'
import { useTranslation } from 'react-i18next'
import { formatNumber } from '@dataplace.ai/functions/utils'
import { useCheckForMobileDevice } from '@dataplace.ai/ui-components/themes'
import { calculateDirection, Comparison, Loader, Tooltip } from 'libs/shared/ui-components/src/atoms'
import { useSelector } from 'react-redux'
import { RootState } from 'apps/placeme/src/redux/store'
import { ComparedLocationBean } from '@dataplace.ai/ui-components/molecules'
import { ISpendingRow, ISpendingTileData } from './@types/ISpendingTileData'
import { spendingTableData, spendingTableDataForeign } from './data'

const Wrapper = styled.div(({ theme }) => {
  const { palette } = theme
  return css`
    display: grid;
    flex-direction: column;
    padding: 1.25rem 1.5rem;
    background-color: ${palette.light.white};
  `
})

const CategorySpan = styled.span(({ theme }) => {
  const { typography } = theme
  return css`
    font-size: ${typography.small.pt_13_semibold.fontSize};
    font-weight: ${typography.small.pt_13_semibold.fontWeight};
    line-height: ${typography.small.pt_13_semibold.lineHeight};

  `
})

const RegularSpan = styled.span(({ theme }) => {
  const { typography } = theme
  return css`
    display: flex !important;
    align-items: center;
    justify-content: flex-end;
    font-size: ${typography.small.pt_13_regular.fontSize};
    font-weight: ${typography.small.pt_13_regular.fontWeight};
    line-height: ${typography.small.pt_13_regular.lineHeight};
    text-align: right;

    .comparison{
      margin-left: 5px;
    }
    
  `
})

const PercentageDiv = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`

export const SpendingTile: React.FC<{data: ISpendingTileData, }> = ({ data }) => {
  const { t } = useTranslation()
  const isMobileDevice = useCheckForMobileDevice()
  const { value } = useSelector((state: RootState) => state.location)
  const { comparedLocation } = useSelector((state: RootState) => state.analysis)
  const country = useMemo(() => value?.country, [value])
  const labels = country === 'PL' ? spendingTableData.labels : spendingTableDataForeign.labels

  const tooltipContent = (value?: number): JSX.Element => (
    (!value)
      ? <></>
      : (
        <>
          <span>
            {t('placeme.spending_tile.tooltip.first')}
            {' '}
            {value < 0 ? t('placeme.spending_tile.tooltip.less') : t('placeme.spending_tile.tooltip.more')}
            {` ${Math.abs(value).toFixed(1)}% `}
            {country === 'PL' ? t('placeme.spending_tile.tooltip.last') : t('placeme.spending_tile.tooltip.last.foreign')}
          </span>
        </>
      )
  )
  const currency = data?.value?.currency

  const content = Array.isArray(data?.value?.spending)
    ? data?.value?.comparedLocation?.spending
      ? (data?.value?.spending?.map((val: ISpendingRow) => (
        [
          <CategorySpan key={val.categoryName}>{t(`spending.category.${val.categoryName.split(' ').join('_')}`)}</CategorySpan>,
          <RegularSpan
            key={val.perCapita}
          >
            <Tooltip
              content={tooltipContent(val.catchmentVoivodeshipDiff)}
              maxWidth='250px'
              position='left bottom'
            >
              <Comparison
                arrow
                direction={calculateDirection({
                  value: val.perCapita,
                  reference: val.voivodeshipMean,
                  fixTo: 1,
                })}
                value={val.perCapita.toFixed(1)}
                withIcon={false}
              />
            </Tooltip>
            <ComparedLocationBean
              className='comparison'
              comparedAddress={comparedLocation?.location?.address}
              comparedValue={formatNumber(data?.value?.comparedLocation?.spending?.find(item =>
                item?.categoryName === val?.categoryName)?.perCapita || '')}
              difference={data?.value?.comparedLocation?.spending?.find(item =>
                item?.categoryName === val?.categoryName)?.perCapitaDiff}
              mainAddress={value?.address}
              mainValue={formatNumber(val.perCapita)}
              width='64.72px'
            />
          </RegularSpan>,
          <RegularSpan key={val.catchmentSum}>
            {formatNumber(val.catchmentSum)}
            <ComparedLocationBean
              className='comparison'
              comparedAddress={comparedLocation?.location?.address}
              comparedValue={formatNumber(data?.value?.comparedLocation?.spending?.find(item =>
                item?.categoryName === val?.categoryName)?.catchmentSum || '')}
              difference={data?.value?.comparedLocation?.spending?.find(item =>
                item?.categoryName === val?.categoryName)?.catchmentSumDiff}
              mainAddress={value?.address}
              mainValue={formatNumber(val.catchmentSum)}
              width='64.72px'
            />
          </RegularSpan>,
          (data?.value?.comparedLocation?.spending?.find(item =>
            item?.categoryName === val?.categoryName)?.voivodeshipMean)
            ? (
              <PercentageDiv key={val.voivodeshipMean}>
                {val.voivodeshipMean.toFixed(1)}
                {(data?.value?.comparedLocation?.spending?.find(item =>
                  item?.categoryName === val?.categoryName)?.voivodeshipMeanDiff !== 0)
                  ? (
                    <ComparedLocationBean
                      className='comparison'
                      comparedAddress={comparedLocation?.location?.address}
                      comparedValue={`${data?.value?.comparedLocation?.spending?.find(item =>
                        item?.categoryName === val?.categoryName)?.voivodeshipMean.toFixed(1)}`}
                      difference={data?.value?.comparedLocation?.spending?.find(item =>
                        item?.categoryName === val?.categoryName)?.voivodeshipMeanDiff}
                      mainAddress={value?.address}
                      mainValue={`${val.voivodeshipMean.toFixed(1)}`}
                      width='64.72px'
                    />
                  )
                  : (<div />)}
              </PercentageDiv>
            )
            : <div />,
        ]
      ))
      )
      : (data?.value?.spending?.map((value: ISpendingRow) => (
        [
          <CategorySpan key={value.categoryName}>{t(`spending.category.${value.categoryName.split(' ').join('_')}`)}</CategorySpan>,
          <RegularSpan key={value.perCapita}>
            <Tooltip
              content={tooltipContent(value.catchmentVoivodeshipDiff)}
              maxWidth='250px'
              position='left bottom'
            >
              <Comparison
                arrow
                direction={calculateDirection({
                  value: value.perCapita,
                  reference: value.voivodeshipMean,
                  fixTo: 1,
                })}
                value={value.perCapita.toFixed(1)}
                withIcon={false}
              />
            </Tooltip>
          </RegularSpan>,
          <RegularSpan key={value.catchmentSum}>{formatNumber(value.catchmentSum)}</RegularSpan>,
          (value.voivodeshipMean)
            ? (
              <PercentageDiv key={value.voivodeshipMean}>
                {value.voivodeshipMean.toFixed(1)}
              </PercentageDiv>
            )
            : <div />,
        ]
      ))
      )
    : []

  return !data || data?.loading
    ? (<Loader />)
    : (
      <Wrapper>
        <Table
          buildings
          content={content}
          gap={!isMobileDevice ? '1rem' : '0.5rem'}
          headerTemplate={country === 'PL' ? '2fr 2fr 2fr 2fr' : '2fr 2fr 2fr'}
          labels={labels.map(label => (label === 'placeme.spending_tile.table_label_2' || label === 'placeme.spending_tile.table_label_3'
            ? <span key={label}>{`${t(label)} [${currency || 'PLN'}]`}</span>
            : <span key={label}>{t(label)}</span>))}
          rowTemplate={country === 'PL' ? '2fr 2fr 2fr 2fr' : '2fr 2fr 2fr'}
        />
      </Wrapper>
    )
}
